import React from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import AtlasSales1P3PSplineChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSales1P3PSplineChart';
import { useAppSelector } from 'src/utils/Hooks';
import { getAtlasProductTagsTreatment, shouldShowOnePRetailSales } from 'src/utils/app';
import {
  useBusinessUnitEntity,
  useBrandEntity,
  useProductEntity,
  useMainEntityType,
  useSegmentEntity,
  useCategoryEntity,
  useSubcategoryEntity,
  useTrendEntity
} from 'src/utils/Hooks/reduxSelectors';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import AtlasV2DorisSplineChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2DorisSplineChart';
import AtlasV2SalesMarketShareOfRetailSalesChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasV2SalesMarketShareOfRetailSalesChart';
import AtlasSalesProductCountSplineChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesProductCountSplineChart';
import AtlasSales1PTotalSalesSplineChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSales1PTotalSalesSplineChart';
import AtlasSalesDoubleDropdownTopEntityChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesDoubleDropdownTopEntityChart';
import { AtlasSalesV2ProductGrid } from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesV2ProductGrid';
import AtlasSalesMarketShareProductSplineChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesMarketShareProductSplineChart';
import AtlasNon1PMarketShareSplineChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasNon1PMarketShareSplineChart';
import AtlasSalesNon1PDropdownTopEntityChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesNon1PDropdownTopEntityChart';
import AtlasSalesMarketShareColumnChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesMarketShareColumnChart';
import AtlasMarketShareGridV2 from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasMarketShareGridV2';
import AtlasSalesNon1PMarketShareColumnChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesNon1PMarketShareColumnChart';
import { useAtlasV2TabTreatmentRedirect } from 'src/components/AtlasV2Components/Hooks/useAtlasV2TabTreatmentRedirect';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';
import { ChartContextGroupProvider } from 'src/utils/chartContext/ChartContextGroupContext';

export const ONE_P_THRESHOLD = 25;
/**
 * Atlas Sales Summary V2 Page layout - Tab name: sales subtab name: retailSales
 */
export const AtlasSalesSummaryV2PageLayout = () => {
  const skuCount = useAppSelector((state) => state.entityService.mainEntity.skuCount);
  const mainEntityType = useMainEntityType();
  const { isSegmentEntity } = useSegmentEntity();
  const { isProductEntity } = useProductEntity();
  const { isCategoryEntity, isAllCategoriesEntity } = useCategoryEntity();
  const { isSubcategoryEntity } = useSubcategoryEntity();
  const { isBusinessUnitEntity } = useBusinessUnitEntity();
  const { isBrandEntity } = useBrandEntity();
  const { isTrendEntity, trendEntity } = useTrendEntity();
  const { isRedirecting } = useAtlasV2TabTreatmentRedirect();

  const productTagsEnabledCondition =
    isSegmentEntity ||
    isBrandEntity ||
    isBusinessUnitEntity ||
    isSubcategoryEntity ||
    (isCategoryEntity && !isAllCategoriesEntity);

  if (isRedirecting) return null;

  // for segment, if sku count is less than or equal to 25, hide the one P, three P spline chart
  return shouldShowOnePRetailSales() && !(isSegmentEntity && skuCount <= ONE_P_THRESHOLD) && !isTrendEntity ? (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        {/* ── Retail Sales spline chart ── */}
        <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.RETAIL_SALES.name}>
          {isProductEntity ? (
            <AtlasV2DorisSplineChart
              queryKey="RetailSalesSplineChart"
              title="Retail Sales"
              methodName="sales/GetSalesMetrics"
              metricTypeDisplay={METRICTYPE.MONEY}
              metricType="RetailSales"
              splineChartProps={{ chartContextId: CHART_NAMES.RETAIL_SALES_SPLINE }}
            />
          ) : (
            <AtlasSales1P3PSplineChart />
          )}
        </ChartContextGroupProvider>

        {/* ── Market Share of Retail Sales spline chart ── */}
        {(mainEntityType === 'company' || isBrandEntity || isTrendEntity) && (
          <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.MARKET_SHARE_RETAIL_SALES.name}>
            <AtlasV2SalesMarketShareOfRetailSalesChart />
          </ChartContextGroupProvider>
        )}

        {/* ── Product Count spline chart ── */}
        {!isProductEntity && (
          <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.SALES_PRODUCT_COUNT.name}>
            <AtlasSalesProductCountSplineChart />
          </ChartContextGroupProvider>
        )}

        {/* ── 1P Retail Sales as % of Total (company) ── */}
        {mainEntityType === 'company' && (
          <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.SALES_1P_PERCENT.name}>
            <AtlasSales1PTotalSalesSplineChart />
          </ChartContextGroupProvider>
        )}

        {/* ── Market Share column chart ── */}
        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity || isTrendEntity) && (
          <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.SALES_MARKET_SHARE_COLUMN.name}>
            <AtlasSalesMarketShareColumnChart enableSearchableGroupBy={false} />
          </ChartContextGroupProvider>
        )}

        {/* ── Top entity double dropdown (company) ── */}
        {mainEntityType === 'company' && (
          <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.SALES_TOP_ENTITY.name}>
            <AtlasSalesDoubleDropdownTopEntityChart />
          </ChartContextGroupProvider>
        )}

        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          (isTrendEntity && trendEntity?.trendType !== 'brand')) && <AtlasMarketShareGridV2 metricType="retailSales" />}

        {/* ── Top entity double dropdown (category/brand/etc.) ── */}
        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isBrandEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          isTrendEntity) && (
          <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.SALES_TOP_ENTITY_BROAD.name}>
            {/*  Double Dropdown Retail Sales, 1P, 3P by Brand Manufacturer */}
            <AtlasSalesDoubleDropdownTopEntityChart
              enableSearchableGroupBy={productTagsEnabledCondition && getAtlasProductTagsTreatment() === 'on'}
            />
          </ChartContextGroupProvider>
        )}

        {/* ── 1P Retail Sales as % of Total (category/brand/etc.) ── */}
        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isBrandEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          isTrendEntity) && (
          <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.SALES_1P_PERCENT.name}>
            <AtlasSales1PTotalSalesSplineChart />
          </ChartContextGroupProvider>
        )}

        {/* ── Product Market Share spline chart ── */}
        {isProductEntity && (
          <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.SALES_MARKET_SHARE_PRODUCT.name}>
            <AtlasSalesMarketShareProductSplineChart />
          </ChartContextGroupProvider>
        )}

        <AtlasSalesV2ProductGrid salesType="retailSales" />
      </Flex>
    </BasePageContainer>
  ) : (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        {/* ── Retail Sales spline chart ── */}
        <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.RETAIL_SALES.name}>
          <AtlasV2DorisSplineChart
            queryKey="RetailSalesSplineChart"
            title="Retail Sales"
            methodName="sales/GetSalesMetrics"
            metricTypeDisplay={METRICTYPE.MONEY}
            metricType="RetailSales"
            splineChartProps={{ chartContextId: CHART_NAMES.RETAIL_SALES_SPLINE }}
          />
        </ChartContextGroupProvider>

        {/* ── Market Share of Retail Sales spline chart ── */}
        {(mainEntityType === 'company' || isBrandEntity || isProductEntity || isTrendEntity) && (
          <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.MARKET_SHARE_RETAIL_SALES.name}>
            <AtlasNon1PMarketShareSplineChart />
          </ChartContextGroupProvider>
        )}

        {/* ── Product Count spline chart ── */}
        {!isProductEntity && (
          <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.SALES_PRODUCT_COUNT.name}>
            <AtlasSalesProductCountSplineChart />
          </ChartContextGroupProvider>
        )}

        {/* ── Non-1P Market Share column chart ── */}
        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          (isTrendEntity && trendEntity?.trendType !== 'brand')) && (
          <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.SALES_MARKET_SHARE_COLUMN.name}>
            <AtlasSalesNon1PMarketShareColumnChart />
          </ChartContextGroupProvider>
        )}

        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isBusinessUnitEntity ||
          (isTrendEntity && trendEntity?.trendType !== 'brand') ||
          isSegmentEntity ||
          mainEntityType === 'businessunit') && <AtlasMarketShareGridV2 metricType="retailSales" />}

        {/* ── Non-1P top entity dropdown chart ── */}
        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isBrandEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          (isTrendEntity && trendEntity?.trendType !== 'brand')) && (
          <ChartContextGroupProvider value={CHART_CONTEXT_GROUPS.SALES_TOP_ENTITY_BROAD.name}>
            <AtlasSalesNon1PDropdownTopEntityChart
              enableSearchableGroupBy={productTagsEnabledCondition && getAtlasProductTagsTreatment() === 'on'}
            />
          </ChartContextGroupProvider>
        )}

        <AtlasSalesV2ProductGrid salesType="retailSales" />
      </Flex>
    </BasePageContainer>
  );
};
